import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Clock, Shield, CheckCircle, Phone } from "lucide-react";
import SEO from "../components/SEO";
import { getBreadcrumbSchema } from "../utils/schema";
import CalendlyEmbed from "../components/CalendlyEmbed";
import "./Contact.css";

export const Contact = () => {
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Contact', url: '/contact' }
  ]);

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us | Get in Touch with Wolverhampton Accountant | Brightson Accounting"
        description="Contact our FCCA-qualified accountant. Call 07362 982967 or email info@brightsonaccounting.co.uk for expert accounting advice. 24-hour response time guaranteed."
        canonical="/contact"
        keywords="contact accountant Wolverhampton, call accountant Wolverhampton, accounting enquiry, accountant contact details"
        schema={breadcrumbSchema}
      />
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content">
            <h1>Get in Touch with Our Wolverhampton Accountant</h1>
            <p>
              Professional accounting advice and support for your business or
              personal finances. Contact our FCCA-qualified accountant with 15+
              years of experience today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section contact-content">
        <div className="container">
          <div className="contact-layout">
            {/* Main Contact Card */}
            <div className="contact-main-card" data-testid="contact-main-card">
              <h2>Contact Our Accountant</h2>
              <p className="contact-intro">
                All enquiries are handled directly by our FCCA-qualified accountant.
                Choose the option that suits you best — call us for an immediate
                conversation, or email for a detailed written response within 24 hours.
              </p>

              {/* Dual Contact Method Grid: Phone + Email */}
              <div className="contact-method-grid">
                {/* Phone Card */}
                <div className="contact-method-card phone-method-card" data-testid="contact-phone-card">
                  <div className="method-icon-wrapper phone-icon-wrapper">
                    <Phone size={28} />
                  </div>
                  <div className="method-label">Call Us</div>
                  <a
                    href="tel:+447362982967"
                    className="method-value method-value-phone"
                    data-testid="contact-phone-link"
                  >
                    07362 982967
                  </a>
                  <p className="method-meta">Available during UK business hours</p>
                  <a
                    href="tel:+447362982967"
                    className="btn-primary method-cta-btn"
                    data-testid="contact-call-now-btn"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>

                {/* Email Card */}
                <div className="contact-method-card email-method-card" data-testid="contact-email-card">
                  <div className="method-icon-wrapper email-icon-wrapper">
                    <Mail size={28} />
                  </div>
                  <div className="method-label">Email Us</div>
                  <a
                    href="mailto:info@brightsonaccounting.co.uk"
                    className="method-value method-value-email"
                    data-testid="contact-email-link"
                  >
                    info@brightsonaccounting.co.uk
                  </a>
                  <p className="method-meta">Response within 24 hours</p>
                  <a
                    href="mailto:info@brightsonaccounting.co.uk"
                    className="btn-primary method-cta-btn"
                    data-testid="contact-email-now-btn"
                  >
                    <Mail size={18} />
                    Email Us Now
                  </a>
                </div>
              </div>

              <div className="confidentiality-note">
                <Shield size={20} />
                <p>
                  All communications are treated with strict confidentiality in
                  accordance with professional accounting standards.
                </p>
              </div>
            </div>

            {/* Info Sidebar */}
            <div className="contact-sidebar">
              <div className="info-card">
                <div className="info-icon">
                  <MapPin size={24} />
                </div>
                <h4>Registered Office</h4>
                <p>71-75 Shelton Street</p>
                <p>Covent Garden</p>
                <p>London</p>
                <p>United Kingdom</p>
                <p>WC2H 9JQ</p>
                <p className="service-areas">
                  Serving Wolverhampton and the West Midlands — including Dudley,
                  Walsall, Bilston and Willenhall — and clients across the UK.
                </p>
              </div>

              <div className="info-card">
                <div className="info-icon">
                  <Clock size={24} />
                </div>
                <h4>Response Time</h4>
                <p>We aim to respond to all email enquiries within 24 hours during business days.</p>
                <p className="highlight-text">
                  Expect professional, detailed advice from our qualified
                  accountant.
                </p>
              </div>

              <div className="info-card">
                <div className="info-icon">
                  <CheckCircle size={24} />
                </div>
                <h4>What to Include</h4>
                <ul className="include-list">
                  <li>Your name and business/personal status</li>
                  <li>Brief description of your enquiry</li>
                  <li>Any relevant deadlines or timeframes</li>
                  <li>Preferred method for follow-up</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Contact Section */}
      <section className="section section-alt why-email">
        <div className="container">
          <h2 className="text-center mb-4">Why Clients Choose Us</h2>
          <div className="why-email-grid">
            <div className="reason-card">
              <h4>Detailed Responses</h4>
              <p>
                Every enquiry receives thoughtful, comprehensive advice with
                proper consideration of your specific circumstances.
              </p>
            </div>
            <div className="reason-card">
              <h4>Written Record</h4>
              <p>
                You receive professional advice in writing, providing a clear
                record of recommendations and guidance for future reference.
              </p>
            </div>
            <div className="reason-card">
              <h4>Flexible Timing</h4>
              <p>
                Get in touch at any time that suits you. We respond during
                business hours with full attention to your query.
              </p>
            </div>
            <div className="reason-card">
              <h4>Accuracy & Precision</h4>
              <p>
                Clear communication ensures all details, figures, and advice
                are accurate and properly documented for compliance purposes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary CTA Section */}
      <section className="section contact-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Speak with an Accountant?</h2>
            <p>
              Our FCCA-qualified accountant is ready to help with expert advice
              on all aspects of business and personal accounting.
            </p>
            <Link
              to="/contact"
              className="btn-primary btn-large"
              data-testid="contact-secondary-cta"
            >
              <Mail size={22} />
              Send Your Enquiry
            </Link>
          </div>
        </div>
      </section>

      {/* Calendar Booking Section */}
      <section className="section calendar-booking-section" id="calendar">
        <div className="container">
          <div className="calendar-booking-content">
            <div className="calendar-heading-wrapper">
              <h2 className="calendar-heading">Prefer to book a call?</h2>
              <p className="calendar-subtext">
                Book a quick 10–15 minute call with our accountant at a time that suits you.
              </p>
            </div>

            {/* Calendly Embed */}
            <CalendlyEmbed
              url="https://calendly.com/brightsonaccounting"
              height="700px"
            />

            <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#666', marginTop: '1rem' }}>
              All calls are handled by our FCCA-qualified accountant with strict confidentiality.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
